import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box
} from '@mui/material';
import axios from 'axios';
import { useAuth, UserContextType } from '../auth';
import { useContext } from 'react';
import { baseBackendUrl } from '../service/baseService';
const Emitter = require("tiny-emitter/instance");

interface PrayerRequest {
  id: number;
  title: string;
}

interface PrayerRequestModalProps {
  open: boolean;
  onClose: () => void;
}

const PrayerRequestModal: React.FC<PrayerRequestModalProps> = ({ open, onClose }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [relatedPrayerRequestId, setRelatedPrayerRequestId] = useState<number | ''>('');
  const [prayerRequests, setPrayerRequests] = useState<PrayerRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const auth = useContext<UserContextType>(useAuth);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      if (!auth.IsAuthenticated?.()) {
        onClose();
        Emitter.emit("notify", "You must be logged in to submit a prayer request.", "error");
        navigate('/login');
        return;
      }
      fetchPrayerRequests();
    }
  }, [open]);

  const fetchPrayerRequests = async () => {
    try {
      const token = auth.getToken?.();
      if (!token) {
        console.error('No token available');
        return;
      }

      console.log('Token:', token); // Debug log
      console.log('Request URL:', `${baseBackendUrl}/api/prayer-requests`); // Debug log

      const response = await axios.get(`${baseBackendUrl}/api/prayer-requests`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPrayerRequests(response.data);
    } catch (error) {
      console.error('Error fetching prayer requests:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const token = auth.getToken?.();
      if (!token) {
        console.error('No token available');
        return;
      }

      console.log('Token:', token); // Debug log
      console.log('Request URL:', `${baseBackendUrl}/api/prayer-requests`); // Debug log
      console.log('Request Body:', {
        Title: title,
        Description: description,
        RelatedPrayerRequestId: relatedPrayerRequestId || 0,
      }); // Debug log

      await axios.post(
        `${baseBackendUrl}/api/prayer-requests`,
        {
          Title: title,
          Description: description,
          RelatedPrayerRequestId: relatedPrayerRequestId || 0,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Reset form and close modal
      setTitle('');
      setDescription('');
      setRelatedPrayerRequestId('');
      onClose();
    } catch (error) {
      console.error('Error submitting prayer request:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Submit Prayer Request</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            multiline
            rows={4}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Related Prayer Request</InputLabel>
            <Select
              value={relatedPrayerRequestId}
              onChange={(e) => setRelatedPrayerRequestId(e.target.value as number)}
              label="Related Prayer Request"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {prayerRequests.map((request) => (
                <MenuItem key={request.id} value={request.id}>
                  {request.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrayerRequestModal; 